// Streaming client for POST /api/ai/generate-document?stream=1 — Phase 1 of
// docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md (§5.2 C1).
//
// The reply is shown as it is written, like ChatGPT. The server sends:
//   event: delta  data: {"text": "..."}        — the next piece of visible text
//   event: done   data: {content, suggestions, provider, model}
//   event: error  data: {"error": "...", "code"?: "..."}
// `done.content` is authoritative: the streamed text is only a preview, and
// the caller should replace it with `content` once it arrives (the server
// strips the suggestions block and re-identifies the patient on the whole
// reply, which can differ slightly from the concatenated deltas).
//
// Native EventSource can't send a POST body or an Authorization header, so
// this reads the response body directly — the same approach as
// lib/platformApi/provisionStream.ts. A response that isn't an event stream
// (a 403 from the AI policy or consent gate, which the server returns before
// streaming starts) is handled as ordinary JSON.
import { authFetch } from '@/api';

export type GenerationCapability = 'smart_fields' | 'suggestions' | 'chat_reply' | 'memory';

export type GenerationSuggestion =
  | { type: 'add'; label: string; prompt: string }
  | { type: 'question'; label: string; options: string[] }
  // Phase 2 (Doctor AI Memory, §4.4 C8): "💾 Always do this" — one click
  // saves `statement` as an active doctor_ai_memory row via memoryApi.ts.
  | { type: 'remember'; label: string; statement: string };

/** A Doctor AI Memory preference actually folded into this draft (§4.4's "✨ Personalised with N of your preferences"). */
export interface AppliedMemoryItem {
  id: number;
  version: number;
  category: string;
  statement: string;
}

export interface GenerationResult {
  content: string;
  suggestions: GenerationSuggestion[];
  applied_memory?: AppliedMemoryItem[];
  /** ED only: the AI Assistant Role profile that shaped this draft (e.g. the Referral Letter profile). */
  applied_profile?: { name: string; doc_type: string };
  provider?: string;
  model?: string;
}

export interface GenerateDocumentRequest {
  patient: Record<string, unknown>;
  vitals?: Record<string, unknown>;
  messages: { role: string; content: string }[];
  doc_type: string;
  extra_context?: string;
  model?: string;
  temperature?: number;
  max_tokens?: number;
  capabilities?: GenerationCapability[];
  /** ED document assistant: the server builds the clinical context from this
   * encounter (extra_context is then ignored) and uses the ED prompt. */
  er_encounter_id?: number;
}

/** Carries the server's machine-readable `code` (e.g. AI_CONSENT_REQUIRED) when there is one. */
export class GenerationError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, opts: { code?: string; status?: number } = {}) {
    super(message);
    this.name = 'GenerationError';
    this.code = opts.code;
    this.status = opts.status;
  }
}

interface StreamOptions {
  /** Called with the full visible text so far, and the piece just added. */
  onDelta?: (textSoFar: string, piece: string) => void;
  signal?: AbortSignal;
}

// The server's own delta frames arrive in whatever batch size the provider
// and network happen to deliver — often several words or a whole sentence at
// once, which reads as the text "flashing" in rather than being typed. Real
// token-by-token delivery isn't the fix (that would just flash faster or
// slower depending on the provider) — what reads as natural, ChatGPT-style
// typing is decoupling *arrival* from *reveal*: every incoming piece is
// queued and drained onto the caller's `onDelta` one word at a time at a
// steady pace, regardless of how bursty the underlying stream is.
const REVEAL_DELAY_MS = 18;
// Once the server's own 'done' event arrives, the queue is drained at this
// much faster pace instead of stopped outright — the visible text still
// finishes by typing the rest, just quickly, rather than jumping straight to
// the final content.
const REVEAL_FLUSH_DELAY_MS = 3;

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
  });
}

// Splits on run boundaries (leading whitespace + the run of non-whitespace
// that follows it) so each queued chunk is one "word" worth of typing, not a
// single character (too slow to read) or a whole delta frame (too bursty).
function splitIntoWords(piece: string): string[] {
  const chunks: string[] = [];
  let i = 0;
  while (i < piece.length) {
    let j = i;
    while (j < piece.length && /\s/.test(piece[j]!)) j++;
    while (j < piece.length && !/\s/.test(piece[j]!)) j++;
    if (j === i) j = i + 1; // never stall on an unexpected zero-width match
    chunks.push(piece.slice(i, j));
    i = j;
  }
  return chunks;
}

function parseFrame(frame: string): { event: string; data: unknown } | null {
  let event = 'message';
  let data = '';
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!data) return null;
  try {
    return { event, data: JSON.parse(data) };
  } catch {
    return null;
  }
}

export async function generateDocument(body: GenerateDocumentRequest, { onDelta, signal }: StreamOptions = {}): Promise<GenerationResult> {
  const res = await authFetch('/api/ai/generate-document?stream=1', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  const isStream = (res.headers.get('content-type') || '').includes('text/event-stream');
  if (!isStream || !res.body) {
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new GenerationError(payload.error || `Request failed (${res.status})`, { code: payload.code, status: res.status });
    }
    return { content: payload.content ?? '', suggestions: payload.suggestions ?? [], applied_memory: payload.applied_memory ?? [], applied_profile: payload.applied_profile, provider: payload.provider, model: payload.model };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';

  // The reveal queue/drain loop below is what paces onDelta — see the
  // REVEAL_DELAY_MS comment above for why this exists. `revealed` is the
  // text actually handed to the caller so far, which can lag behind `text`
  // (the raw, fully-arrived accumulation used for the no-'done' fallback and
  // the abort/error paths below, where there's no reason to wait out the
  // pacing before surfacing what was received).
  let revealed = '';
  const queue: string[] = [];
  let draining = false;
  let flushing = false;
  let drainResolve: (() => void) | null = null;

  const waitForDrain = () => (queue.length || draining ? new Promise<void>((resolve) => { drainResolve = resolve; }) : Promise.resolve());

  async function drain() {
    if (draining) return;
    draining = true;
    while (queue.length) {
      if (signal?.aborted) break;
      const chunk = queue.shift()!;
      revealed += chunk;
      onDelta?.(revealed, chunk);
      await delay(flushing ? REVEAL_FLUSH_DELAY_MS : REVEAL_DELAY_MS, signal);
    }
    draining = false;
    drainResolve?.();
    drainResolve = null;
  }

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = parseFrame(buffer.slice(0, sep));
      buffer = buffer.slice(sep + 2);
      if (!frame) continue;

      if (frame.event === 'delta') {
        const piece = String((frame.data as { text?: string }).text ?? '');
        if (piece) {
          text += piece;
          queue.push(...splitIntoWords(piece));
          void drain();
        }
      } else if (frame.event === 'done') {
        const d = frame.data as Partial<GenerationResult>;
        // Let the queued words finish typing out, just quickly, rather than
        // jumping straight from a partial preview to the final content.
        flushing = true;
        await waitForDrain();
        return { content: d.content ?? text, suggestions: d.suggestions ?? [], applied_memory: d.applied_memory ?? [], applied_profile: d.applied_profile, provider: d.provider, model: d.model };
      } else if (frame.event === 'error') {
        const e = frame.data as { error?: string; code?: string };
        throw new GenerationError(e.error || 'The AI request failed', { code: e.code });
      }
    }
  }

  throw new GenerationError('The AI response was interrupted before it finished. Please try again.');
}
